import { FC, useEffect, useRef } from 'react';

import {
	Box,
	Button,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	VStack,
} from '@bonusly/design-system';

import { getSeasonConfig } from './get-season-config';

interface SeasonalThemeModalProps {
	isOpen: boolean;
	onClose: () => void;
	onEnable: () => void;
}

/**
 * Modal that displays when users click to enable the seasonal theme
 * Shows fun information about the current seasonal theme features
 * Automatically uses assets and colors from the active season configuration
 */
export const SeasonalThemeModal: FC<SeasonalThemeModalProps> = ({ isOpen, onClose, onEnable }) => {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const seasonConfig = getSeasonConfig();

	// Play sound when modal opens, stop when it closes
	useEffect(() => {
		if (isOpen && seasonConfig.modalSound) {
			// Create and play audio when modal opens
			audioRef.current = new Audio(seasonConfig.modalSound);
			audioRef.current.volume = seasonConfig.modalSoundVolume;
			audioRef.current.play().catch((error) => {
				console.warn('Failed to play seasonal sound:', error);
			});
		} else {
			// Stop and cleanup audio when modal closes
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current.currentTime = 0;
				audioRef.current = null;
			}
		}

		// Cleanup on unmount
		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, [isOpen, seasonConfig.modalSound, seasonConfig.modalSoundVolume]);

	const handleEnable = () => {
		onEnable();
		onClose();
	};

	const { modalColors, modalDecorations, logoDark, modalText } = seasonConfig;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
			<ModalOverlay bg={modalColors.overlayBg} backdropFilter="blur(4px)" />
			<ModalContent
				bg={modalColors.contentBg}
				bgImage={modalColors.contentBgImage}
				bgSize={modalColors.contentBgSize}
				bgPosition={modalColors.contentBgPosition}
				bgRepeat={modalColors.contentBgRepeat}
				borderRadius="xl"
				boxShadow={modalColors.boxShadow}
				border="1px solid"
				borderColor={modalColors.borderColor}
				overflow="hidden"
			>
				{/* Decorative elements */}
				{modalDecorations.map((decoration, index) => (
					<Box
						key={index}
						position="absolute"
						top={decoration.top}
						right={decoration.right}
						bottom={decoration.bottom}
						left={decoration.left}
						fontSize={decoration.fontSize}
						opacity={decoration.opacity}
						transform={`rotate(${decoration.rotation}deg)`}
					>
						{decoration.emoji}
					</Box>
				))}

				<ModalHeader pt={8} pb={4} px={6} position="relative" zIndex={1}>
					<VStack spacing={3} align="center">
						<Box
							as="img"
							src={logoDark}
							alt="Seasonal Bonusly Logo"
							maxW="220px"
							w="100%"
							h="auto"
							filter={`drop-shadow(0 4px 15px ${modalColors.logoDropShadowColor})`}
						/>
					</VStack>
					<ModalCloseButton
						color={modalColors.cancelButtonColor}
						_hover={{ bg: 'whiteAlpha.200', color: modalColors.cancelButtonHoverColor }}
						top={4}
						right={4}
						size="lg"
					/>
				</ModalHeader>

				<ModalBody px={6} pb={4} position="relative" zIndex={1}>
					<VStack spacing={4} align="stretch">
						<Text fontSize="md" color={modalColors.subtitleColor} textAlign="center" lineHeight="tall" fontWeight="medium" my={4}>
							{modalText.subtitle}
						</Text>

						<VStack spacing={3} align="stretch">
							{modalText.features.map((feature, index) => (
								<HStack key={index} spacing={3} align="start" bg="whiteAlpha.50" p={2.5} borderRadius="md" border="1px solid" borderColor="whiteAlpha.100">
									<Text fontSize="lg" flexShrink={0}>
										{feature.emoji}
									</Text>
									<Text fontSize="xs" color="whiteAlpha.900" lineHeight="tall">
										{feature.text}
									</Text>
								</HStack>
							))}
						</VStack>

						<Box bg={modalColors.noticeBg} borderRadius="md" p={3} border="1px solid" borderColor={modalColors.noticeBorderColor} mt={1}>
							<Text fontSize="xs" color={modalColors.noticeTextColor} textAlign="center" fontStyle="italic">
								{modalText.endDateNotice}
							</Text>
						</Box>
					</VStack>
				</ModalBody>

				<ModalFooter px={6} pb={6} pt={3} position="relative" zIndex={1} flexDirection="column" gap={2}>
					<Button
						onClick={handleEnable}
						size="md"
						width="full"
						bg={modalColors.enableButtonBg}
						color={modalColors.enableButtonTextColor || 'white'}
						fontWeight="semibold"
						fontSize="md"
						_hover={{
							bg: modalColors.enableButtonHoverBg,
							transform: 'translateY(-1px)',
							boxShadow: modalColors.enableButtonHoverBoxShadow,
						}}
						_active={{
							bg: modalColors.enableButtonActiveBg,
							transform: 'translateY(0)',
						}}
						transition="all 0.2s"
						boxShadow={modalColors.enableButtonBoxShadow}
					>
						{modalText.enableButtonText} {seasonConfig.toggleEmoji}
					</Button>

					<Button
						onClick={onClose}
						size="sm"
						width="full"
						variant="ghost"
						color={modalColors.cancelButtonColor}
						_hover={{
							bg: 'whiteAlpha.100',
							color: modalColors.cancelButtonHoverColor,
						}}
					>
						{modalText.cancelButtonText}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
